/**
 * Medicine Data Structure Utilities
 * 
 * Defines the complete medicine table structure and provides utilities
 * for handling medicine data throughout the application.
 * 
 * Table Structure: No. | Medicine | Type | Dosage | Frequency | Duration | How to take | From To | Action
 */

/**
 * Default medicine data structure
 * Represents a single medicine entry with all required fields
 */
export const DEFAULT_MEDICINE_STRUCTURE = {
  no: 0,                    // Serial number (auto-generated)
  name: "",                 // Medicine name (e.g., "Paracetamol 500mg")
  type: "",                 // Medicine type (Tablet, Capsule, Syrup, etc.)
  dosage: "",               // Dosage amount (1, 2, 3, etc.)
  frequency: "",            // Frequency pattern (|0|0 = Morning & Evening)
  duration: "",             // Duration (1 day, 1 week, 1 month, etc.)
  howToTake: "",            // Instructions (after food, before food, etc.)
  fromTo: "",               // Date range (auto-calculated from duration)
  // Medicine component state management - each medicine maintains its own allergy state
  allergyState: {           // Separate state for tracking medicine-specific allergies
    isChecked: false,       // Whether allergy check has been performed
    hasAllergies: false,    // Whether patient has allergies to this medicine
    allergyComponents: [],  // Specific components causing allergies
    riskLevel: "none",      // Risk assessment: none, low, medium, high
    lastChecked: null,      // Timestamp of last check
    notes: ""               // Additional notes about allergies
  }
};

/**
 * Medicine type options for dropdown selection
 */
export const MEDICINE_TYPES = [
  { value: "", label: "Select Type" },
  { value: "Tablet", label: "Tablet" },
  { value: "Capsule", label: "Capsule" },
  { value: "teaspoon", label: "teaspoon" },
  { value: "ml", label: "ml" },
  { value: "Injection", label: "Injection" },
  { value: "Eye Drop", label: "Eye Drop" },
  { value: "Eye Ointment", label: "Eye Ointment" },
  { value: "Skin Ointment", label: "Skin Ointment" },
];

/**
 * Dosage options (1-30)
 */
export const DOSAGE_OPTIONS = Array.from({ length: 30 }, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString()
}));

/**
 * Frequency patterns with visual representation
 * Pattern: Morning | Afternoon | Evening | Night
 * 0 = no dose, | = dose
 */
export const FREQUENCY_PATTERNS = [
  { value: "", label: "Select Frequency" },
  { value: "0000", label: "0 - 0 - 0 - 0", description: "No dose" },
  { value: "000|", label: "0 - 0 - 0 - |", description: "Night only" },
  { value: "00|0", label: "0 - 0 - | - 0", description: "Evening only" },
  { value: "00||", label: "0 - 0 - | - |", description: "Evening, Night" },
  { value: "0|00", label: "0 - | - 0 - 0", description: "Afternoon only" },
  { value: "0|0|", label: "0 - | - 0 - |", description: "Afternoon, Night" },
  { value: "0||0", label: "0 - | - | - 0", description: "Afternoon, Evening" },
  { value: "0|||", label: "0 - | - | - |", description: "Afternoon, Evening, Night" },
  { value: "|000", label: "| - 0 - 0 - 0", description: "Morning only" },
  { value: "|00|", label: "| - 0 - 0 - |", description: "Morning, Night" },
  { value: "|0|0", label: "| - 0 - | - 0", description: "Morning, Evening" },
  { value: "|0||", label: "| - 0 - | - |", description: "Morning, Evening, Night" },
  { value: "||00", label: "| - | - 0 - 0", description: "Morning, Afternoon" },
  { value: "||0|", label: "| - | - 0 - |", description: "Morning, Afternoon, Night" },
  { value: "|||0", label: "| - | - | - 0", description: "Morning, Afternoon, Evening" },
  { value: "||||", label: "| - | - | - |", description: "Morning, Afternoon, Evening, Night" },
];

/**
 * Special frequency options for Eye Drops
 */
export const EYE_DROP_FREQUENCIES = [
  { value: "OD", label: "OD (Once a day)" },
  { value: "BD", label: "BD (Twice a day)" },
  { value: "TID", label: "TID (Three times a day)" },
  { value: "HS", label: "HS (Once at night)" },
  { value: "1Hrly", label: "1 Hrly (Every hour)" },
  { value: "2Hrly", label: "2 Hrly (Every 2 hours)" },
  { value: "halfHrly", label: "Half Hrly (Every 30 minutes)" },
  { value: "6TimesADay", label: "6 Times a Day" },
  { value: "QID", label: "QID (Four times a day)" },
  { value: "8TimesADay", label: "8 Times a Day" },
];

/**
 * Special frequency options for Eye Ointment (same as Eye Drops)
 */
export const EYE_OINTMENT_FREQUENCIES = [
  { value: "OD", label: "OD (Once a day)" },
  { value: "BD", label: "BD (Twice a day)" },
  { value: "TID", label: "TID (Three times a day)" },
  { value: "HS", label: "HS (Once at night)" },
  { value: "1Hrly", label: "1 Hrly (Every hour)" },
  { value: "2Hrly", label: "2 Hrly (Every 2 hours)" },
  { value: "halfHrly", label: "Half Hrly (Every 30 minutes)" },
  { value: "6TimesADay", label: "6 Times a Day" },
  { value: "QID", label: "QID (Four times a day)" },
  { value: "8TimesADay", label: "8 Times a Day" },
];

/**
 * Special frequency options for Skin Ointment (same as Eye Drops)
 */
export const SKIN_OINTMENT_FREQUENCIES = [
  { value: "OD", label: "OD (Once a day)" },
  { value: "BD", label: "BD (Twice a day)" },
  { value: "TID", label: "TID (Three times a day)" },
  { value: "HS", label: "HS (Once at night)" },
  { value: "1Hrly", label: "1 Hrly (Every hour)" },
  { value: "2Hrly", label: "2 Hrly (Every 2 hours)" },
  { value: "halfHrly", label: "Half Hrly (Every 30 minutes)" },
  { value: "6TimesADay", label: "6 Times a Day" },
  { value: "QID", label: "QID (Four times a day)" },
  { value: "8TimesADay", label: "8 Times a Day" },
];

/**
 * Duration options for medicine intake
 */
export const DURATION_OPTIONS = [
  { value: "", label: "Select Duration" },
  { value: "1 day", label: "1 day" },
  { value: "2 days", label: "2 days" },
  { value: "3 days", label: "3 days" },
  { value: "4 days", label: "4 days" },
  { value: "5 days", label: "5 days" },
  { value: "6 days", label: "6 days" },
  { value: "1 week", label: "1 week" },
  { value: "2 weeks", label: "2 weeks" },
  { value: "3 weeks", label: "3 weeks" },
  { value: "1 month", label: "1 month" },
  { value: "1.5 month", label: "1.5 month" },
  { value: "2 months", label: "2 months" },
  { value: "3 months", label: "3 months" },
  { value: "6 months", label: "6 months" },
];

/**
 * How to take options for medicine instructions
 */
export const HOW_TO_TAKE_OPTIONS = [
  { value: "", label: "Select How to take" },
  { value: "after food", label: "after food" },
  { value: "before sleep", label: "before sleep" },
  { value: "before food", label: "before food" },
  { value: "with food", label: "with food" },
  { value: "with water", label: "with water" },
  { value: "with milk", label: "with milk" },
];

/**
 * Risk levels for medicine allergies
 */
export const ALLERGY_RISK_LEVELS = [
  { value: "none", label: "No Risk", color: "#10B981", description: "No known allergies" },
  { value: "low", label: "Low Risk", color: "#F59E0B", description: "Minor sensitivities" },
  { value: "medium", label: "Medium Risk", color: "#EF4444", description: "Moderate allergies" },
  { value: "high", label: "High Risk", color: "#DC2626", description: "Severe allergies" }
];

/**
 * Frequency mapping for display purposes
 * Maps 4-character patterns to readable text
 */
export const FREQUENCY_MAPPING = {
  '0000': 'No dose',
  '000|': 'Night only',
  '00|0': 'Evening only', 
  '00||': 'Evening, Night',
  '0|00': 'Afternoon only',
  '0|0|': 'Afternoon, Night',
  '0||0': 'Afternoon, Evening',
  '0|||': 'Afternoon, Evening, Night',
  '|000': 'Morning only',
  '|00|': 'Morning, Night',
  '|0|0': 'Morning, Evening',
  '|0||': 'Morning, Evening, Night',
  '||00': 'Morning, Afternoon',
  '||0|': 'Morning, Afternoon, Night',
  '|||0': 'Morning, Afternoon, Evening',
  '||||': 'Morning, Afternoon, Evening, Night'
};

/**
 * Get display text for frequency pattern
 * @param {string} code - 4-character frequency pattern
 * @returns {string} - Readable frequency text
 */
export const getFrequencyDisplay = (code) => {
  if (!code || code.length !== 4) return code;
  return code.split('').join(' - ');
};

/**
 * Get readable text for frequency pattern
 * @param {string} code - 4-character frequency pattern
 * @returns {string} - Human-readable frequency description
 */
export const getFrequencyText = (code) => {
  const mapping = {
    '0000': 'No dose',
    '000|': 'Night',
    '00|0': 'Evening',
    '00||': 'Evening, Night',
    '0|00': 'Afternoon',
    '0|0|': 'Afternoon, Night',
    '0||0': 'Afternoon, Evening',
    '0|||': 'Afternoon, Evening, Night',
    '|000': 'Morning',
    '|00|': 'Morning, Night',
    '|0|0': 'Morning, Evening',
    '|0||': 'Morning, Evening, Night',
    '||00': 'Morning, Afternoon',
    '||0|': 'Morning, Afternoon, Night',
    '|||0': 'Morning, Afternoon, Evening',
    '||||': 'Morning, Afternoon, Evening, Night'
  };
  return mapping[code] || code;
};

/**
 * Validate medicine data structure
 * @param {Object} medicine - Medicine object to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateMedicineData = (medicine) => {
  const errors = [];
  
  if (!medicine.name || medicine.name.trim() === '') {
    errors.push('Medicine name is required');
  }
  
  if (!medicine.type || medicine.type.trim() === '') {
    errors.push('Medicine type is required');
  }
  
  if (!medicine.dosage || medicine.dosage.trim() === '') {
    errors.push('Dosage is required');
  }
  
  if (!medicine.frequency || medicine.frequency.trim() === '') {
    errors.push('Frequency is required');
  }
  
  if (!medicine.duration || medicine.duration.trim() === '') {
    errors.push('Duration is required');
  }
  
  if (!medicine.howToTake || medicine.howToTake.trim() === '') {
    errors.push('How to take instruction is required');
  }
  
  if (!medicine.fromTo || medicine.fromTo.trim() === '') {
    errors.push('Date range is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Create a new medicine object with default values
 * @param {number} serialNumber - Serial number for the medicine
 * @returns {Object} - New medicine object with default structure
 */
export const createNewMedicine = (serialNumber = 1) => {
  return {
    ...DEFAULT_MEDICINE_STRUCTURE,
    no: serialNumber
  };
};

/**
 * Format medicine data for API submission
 * @param {Object} medicine - Medicine object to format
 * @returns {Object} - Formatted medicine data for API
 */
export const formatMedicineForAPI = (medicine) => {
  return {
    name: medicine.name.trim(),
    type: medicine.type,
    dosage: medicine.dosage,
    frequency: medicine.frequency,
    duration: medicine.duration,
    howToTake: medicine.howToTake,
    fromTo: medicine.fromTo,
    // Additional fields for API compatibility
    frequencyText: getFrequencyText(medicine.frequency),
    frequencyDisplay: getFrequencyDisplay(medicine.frequency)
  };
};

/**
 * Format medicine data for PDF generation
 * @param {Object} medicine - Medicine object to format
 * @returns {Object} - Formatted medicine data for PDF
 */
export const formatMedicineForPDF = (medicine) => {
  return {
    name: medicine.name || "Medicine name",
    dosage: medicine.dosage || "1",
    duration: medicine.duration || "5 days",
    // For PDF, we use readable frequency text
    frequency: getFrequencyText(medicine.frequency) || "As needed",
    howToTake: medicine.howToTake || "As directed",
    // Include allergy warning if present
    allergyWarning: medicine.allergyState?.hasAllergies ? 
      `⚠️ Allergy Risk: ${medicine.allergyState.riskLevel.toUpperCase()}` : null
  };
};

/**
 * Medicine Allergy Management Utilities
 * Functions for managing medicine allergies and their separate states
 */

/**
 * Initialize allergy state for a medicine
 * @param {Object} medicine - Medicine object
 * @returns {Object} - Medicine with initialized allergy state
 */
export const initializeAllergyState = (medicine) => {
  return {
    ...medicine,
    allergyState: {
      isChecked: false,
      hasAllergies: false,
      allergyComponents: [],
      riskLevel: "none",
      lastChecked: null,
      notes: ""
    }
  };
};

/**
 * Update allergy state for a medicine
 * @param {Object} medicine - Medicine object
 * @param {Object} updates - Updates to apply to allergy state
 * @returns {Object} - Updated medicine object
 */
export const updateAllergyState = (medicine, updates) => {
  return {
    ...medicine,
    allergyState: {
      ...medicine.allergyState,
      ...updates,
      lastChecked: new Date().toISOString()
    }
  };
};

/**
 * Check if a medicine has allergies based on patient allergies
 * @param {Object} medicine - Medicine object
 * @param {Array} patientAllergies - Array of medicines patient is allergic to
 * @returns {Object} - Analysis result with risk assessment
 */
export const analyzeMedicineAllergies = (medicine, patientAllergies = []) => {
  if (!patientAllergies.length) {
    return {
      hasRisk: false,
      riskLevel: "none",
      problematicComponents: [],
      recommendations: []
    };
  }

  // Check if medicine name matches any patient allergies
  const hasDirectMatch = patientAllergies.some(allergy => 
    medicine.name.toLowerCase().includes(allergy.toLowerCase()) ||
    allergy.toLowerCase().includes(medicine.name.toLowerCase())
  );

  if (!hasDirectMatch) {
    return {
      hasRisk: false,
      riskLevel: "none",
      problematicComponents: [],
      recommendations: []
    };
  }

  // Determine risk level - for now, any match is considered medium risk
  const riskLevel = "medium";

  // Generate recommendations
  const recommendations = [{
    component: medicine.name,
    issue: `Patient has known allergy to ${medicine.name}`,
    action: "Consider alternative medicine or consult with allergist"
  }];

  return {
    hasRisk: true,
    riskLevel,
    problematicComponents: [medicine.name],
    recommendations
  };
};

/**
 * Generate allergy summary for a medicine
 * @param {Object} medicine - Medicine object with allergy data
 * @returns {String} - Human-readable summary
 */
export const generateAllergySummary = (medicine) => {
  if (!medicine.allergyState?.hasAllergies || medicine.allergyState.riskLevel === "none") {
    return "No allergy concerns";
  }
  
  const { riskLevel, allergyComponents, notes } = medicine.allergyState;
  const riskInfo = ALLERGY_RISK_LEVELS.find(level => level.value === riskLevel);
  
  let summary = `${riskInfo.label}: ${riskInfo.description}`;
  
  if (allergyComponents.length) {
    summary += `. Allergic components: ${allergyComponents.join(", ")}`;
  }
  
  if (notes.trim()) {
    summary += `. Notes: ${notes}`;
  }
  
  return summary;
};